import { useState, useMemo } from 'react';
import { MapPin, Hotel, DollarSign, ArrowRight, Plane } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useLang } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import 'leaflet/dist/leaflet.css';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon } from 'leaflet';

// Custom icons
const airportIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const destinationIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

export default function MapSection() {
  const { airports, destinations, hotels, routePrices, loading } = useData();
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const { t, lang } = useLang();

  const getHotelsByZone = (zoneId) => {
    return hotels.filter(h => h.zone_id === zoneId);
  };

  const getPriceFromAirport = (airportId, destinationId) => {
    const route = routePrices.find(r => r.origin_id === airportId && r.destination_id === destinationId);
    if (route) return route.price;
    const dest = destinations.find(d => d.id === destinationId);
    return dest?.price || 0;
  };

  const scrollToBooking = () => {
    const booking = document.getElementById('booking');
    if (booking) booking.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="destinations" className="py-24 bg-[#112240]" data-testid="map-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-cyan-400 text-sm uppercase tracking-[0.2em] mb-4 font-semibold">
            {t('map_label')}
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mb-6">
            {t('map_title_1')} <span className="gradient-text">{t('map_title_2')}</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            {t('map_desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2 premium-card overflow-hidden" style={{ height: '550px' }}>
            {!loading && (
              <MapContainer
                center={[18.8, -69.5]}
                zoom={8}
                style={{ height: '100%', width: '100%' }}
                className="rounded-xl"
              >
                <TileLayer
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                  attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                />
                {/* Airport markers */}
                {airports.map((airport) => (
                  <Marker
                    key={airport.id}
                    position={[airport.lat, airport.lng]}
                    icon={airportIcon}
                    eventHandlers={{
                      click: () => setSelectedAirport(airport)
                    }}
                  >
                    <Popup>
                      <div className="text-center p-2">
                        <p className="font-bold text-lg flex items-center gap-1">
                          <Plane className="w-4 h-4" />
                          {airport.name}
                        </p>
                        <p className="text-sm text-gray-600">Airport</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                {/* Destination markers */}
                {destinations.map((dest) => (
                  <Marker
                    key={dest.id}
                    position={[dest.lat, dest.lng]}
                    icon={destinationIcon}
                    eventHandlers={{
                      click: () => setSelectedZone(dest)
                    }}
                  >
                    <Popup>
                      <div className="text-center p-2">
                        <p className="font-bold text-lg">{dest.name}</p>
                        <p className="text-orange-500 font-semibold">From ${dest.price} USD</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>

          {/* Side Panel */}
          <div className="premium-card p-6">
            <Tabs defaultValue="airports" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4 bg-white/5">
                <TabsTrigger value="airports" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
                  <Plane className="w-4 h-4 mr-2" />
                  {t('map_tab_airports')}
                </TabsTrigger>
                <TabsTrigger value="destinations" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400">
                  <MapPin className="w-4 h-4 mr-2" />
                  {t('map_tab_destinations')}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="airports">
                <ScrollArea className="h-[420px] pr-4">
                  <div className="space-y-3">
                    {airports.map((airport) => (
                      <button
                        key={airport.id}
                        onClick={() => setSelectedAirport(airport)}
                        className={`w-full p-4 rounded-xl text-left transition-all ${
                          selectedAirport?.id === airport.id
                            ? 'bg-cyan-500/20 border border-cyan-500/50'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                        data-testid={`airport-btn-${airport.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Plane className="w-5 h-5 text-cyan-400" />
                          <span className="font-semibold text-white text-sm">{airport.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
              
              <TabsContent value="destinations">
                <ScrollArea className="h-[420px] pr-4">
                  <div className="space-y-3">
                    {destinations.map((dest) => (
                      <button
                        key={dest.id}
                        onClick={() => setSelectedZone(dest)}
                        className={`w-full p-4 rounded-xl text-left transition-all ${
                          selectedZone?.id === dest.id
                            ? 'bg-orange-500/20 border border-orange-500/50'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                        data-testid={`dest-btn-${dest.id}`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-white text-sm">{dest.name}</span>
                          <span className="text-orange-400 font-bold">${dest.price}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {getHotelsByZone(dest.id).length} hoteles
                        </p>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {/* Price Matrix when airport is selected */}
        {selectedAirport && (
          <div className="mt-8 premium-card p-6" data-testid="price-matrix">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Plane className="w-6 h-6 text-cyan-400" />
                Prices from {selectedAirport.name}
              </h3>
              <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={() => setSelectedAirport(null)}>
                {lang === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {destinations.map((dest) => {
                const price = getPriceFromAirport(selectedAirport.id, dest.id);
                return (
                  <div
                    key={dest.id}
                    className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all"
                    data-testid={`price-card-${dest.id}`}
                  >
                    <h4 className="font-semibold text-white mb-2">{dest.name}</h4>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-orange-400">
                        <DollarSign className="w-4 h-4" />
                        <span className="font-bold text-lg">{price} USD</span>
                      </div>
                      <Button size="sm" className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" onClick={scrollToBooking}>
                        {t('book_now')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected Zone Hotels */}
        {selectedZone && !selectedAirport && (
          <div className="mt-8 premium-card p-6" data-testid="selected-zone-hotels">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                <Hotel className="w-6 h-6 text-cyan-400" />
                Hotels in {selectedZone.name}
                <span className="text-orange-400 text-lg font-normal">
                  (From ${selectedZone.price} USD)
                </span>
              </h3>
              <Button variant="ghost" className="text-slate-400 hover:text-white" onClick={() => setSelectedZone(null)}>
                {lang === 'es' ? 'Cerrar' : 'Close'}
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getHotelsByZone(selectedZone.id).map((hotel) => (
                <div
                  key={hotel.id}
                  className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all"
                  data-testid={`hotel-card-${hotel.id}`}
                >
                  <h4 className="font-semibold text-white mb-2">{hotel.name}</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-orange-400">
                      <DollarSign className="w-4 h-4" />
                      <span className="font-bold">{hotel.price || selectedZone.price} USD</span>
                    </div>
                      <Button
                      size="sm"
                      className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
                      onClick={scrollToBooking}
                      data-testid={`hotel-book-${hotel.id}`}
                    >
                      {t('book_now')} <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-8 flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-blue-500"></div>
            <span className="text-slate-400 text-sm">{t('map_tab_airports')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-orange-500"></div>
            <span className="text-slate-400 text-sm">{t('map_tab_destinations')}</span>
          </div>
        </div>
      </div>
    </section>
  );
}
